import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useEventoCreate } from '@/api/mutations/evento';
import type { TipoEvento, CanalEvento } from '@/api/types';

const TIPOS: TipoEvento[] = [
  'mensagem_enviada',
  'mensagem_recebida',
  'ligacao_feita',
  'ligacao_recebida',
  'email_enviado',
  'email_recebido',
  'call_agendada',
  'call_realizada',
  'call_no_show',
  'proposta_enviada',
  'proposta_aceita',
  'proposta_recusada',
  'nota_adicionada',
  'status_mudou',
];

const CANAIS: CanalEvento[] = [
  'whatsapp',
  'email',
  'ligacao',
  'instagram',
  'linkedin',
  'presencial',
  'sistema',
];

interface EventoFormProps {
  empresaId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function isoNow(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EventoForm({ empresaId, open, onOpenChange }: EventoFormProps) {
  const create = useEventoCreate();
  const [tipo, setTipo] = useState<TipoEvento>('nota_adicionada');
  const [canal, setCanal] = useState<CanalEvento>('sistema');
  const [resumo, setResumo] = useState('');
  const [dataEvento, setDataEvento] = useState(isoNow());

  useEffect(() => {
    if (open) {
      setTipo('nota_adicionada');
      setCanal('sistema');
      setResumo('');
      setDataEvento(isoNow());
    }
  }, [open]);

  async function submit() {
    try {
      await create.mutateAsync({
        empresa_id: empresaId,
        payload: {
          tipo,
          canal,
          payload: resumo ? { resumo } : {},
          data_evento: new Date(dataEvento).toISOString(),
        },
      });
      toast.success('Evento registrado');
      onOpenChange(false);
    } catch (e) {
      toast.error('Falha: ' + (e instanceof Error ? e.message : 'erro'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Registrar evento</h3>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Tipo</label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoEvento)}
                className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                {TIPOS.map((t) => (
                  <option key={t} value={t}>
                    {t.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Canal</label>
              <select
                value={canal}
                onChange={(e) => setCanal(e.target.value as CanalEvento)}
                className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                {CANAIS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Resumo / nota</label>
            <textarea
              value={resumo}
              onChange={(e) => setResumo(e.target.value)}
              placeholder="O que aconteceu, o que foi dito, próximos passos..."
              rows={4}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Quando</label>
            <Input
              type="datetime-local"
              value={dataEvento}
              onChange={(e) => setDataEvento(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
              className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={create.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {create.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              Registrar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
